<script lang="ts" setup>
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';
import { PageHero, ReportProblemLink } from '~/components';
import {
  AppBreadcrumbs,
  AppSkeletonLoader,
  AppTypography,
} from '~/components/base';
import { CampaignDetailModal } from '~/components/campaigns';
import {
  CampaignMiniCard,
  LobbyistRow,
  ProfileCard,
  SourcesDialog,
} from '~/components/candidates';
import { useCandidate } from '~/composables/useCandidate';
import { useCandidateCampaigns } from '~/composables/useCandidateCampaigns';
import { useCandidateLobbyists } from '~/composables/useCandidateLobbyists';
import { $sanitizeHtml } from '~/utils/helpers';
import { normalizeExternalUrl } from '~/utils/normalizeExternalUrl';

definePageMeta({ layout: 'default' });

// Figma: three rows of three cards per page in the 3-across grid.
const CAMPAIGNS_PAGE_SIZE = 9;

type TabValue = 'about' | 'campaigns' | 'lobbyists';
const TABS: { value: TabValue; label: string }[] = [
  { value: 'about', label: 'About' },
  { value: 'campaigns', label: 'Campaigns' },
  { value: 'lobbyists', label: 'Lobbyists' },
];

const route = useRoute();

// Captured once: pages remount per fullPath (see default layout page-key),
// so the id can't legitimately change while this component lives. Reading
// it reactively would flash the not-found state during back-navigation
// (the route empties params just before unmount).
const candidateId = route.params.id as string;

const { candidate, pending: candidatePending } = useCandidate(candidateId);
const { campaigns } = useCandidateCampaigns(candidateId);
const { lobbyists } = useCandidateLobbyists(candidateId);

// Head/meta AFTER the data refs they read — placing them above caused a
// TDZ ReferenceError during client-side navigation.
useHead({
  title: computed(() => candidate.value?.name ?? 'Candidate Profile'),
});
useSeoMeta({
  description: () => candidate.value?.bio ?? 'Candidate profile on VoteWise.',
  ogTitle: () => `${candidate.value?.name ?? 'Candidate Profile'} | VoteWise`,
  ogDescription: () => candidate.value?.bio ?? 'Candidate profile on VoteWise.',
});

const activeTab = ref<TabValue>(
  route.query.tab === 'campaigns' || route.query.tab === 'lobbyists'
    ? (route.query.tab as TabValue)
    : 'about',
);

// The candidate payload carries its party's slug, so link straight off it.
// Matching by name against `useParties()` instead used to drop the button for
// any candidate whose party fell outside that call's first 50 rows (VTW-503).
const partyHref = computed(() =>
  candidate.value?.partySlug ? `/parties/${candidate.value.partySlug}` : null,
);

const breadcrumbs = computed(() => [
  {
    label:
      candidate.value?.chamber === 'upper'
        ? 'Region Candidates'
        : 'District Candidates',
    to: `/candidates?chamber=${candidate.value?.chamber === 'upper' ? 'upper' : 'lower'}`,
  },
  { label: 'Candidate Profile' },
]);

const websiteHref = computed(() =>
  normalizeExternalUrl(candidate.value?.website),
);

const hasWebsite = computed(() => Boolean(candidate.value?.website));
const hasSources = computed(() => Boolean(candidate.value?.sources?.length));

const visibleCampaignsCount = ref(CAMPAIGNS_PAGE_SIZE);
const visibleCampaigns = computed(() =>
  campaigns.value.slice(0, visibleCampaignsCount.value),
);
const hasMoreCampaigns = computed(
  () => visibleCampaignsCount.value < campaigns.value.length,
);

function showMoreCampaigns() {
  visibleCampaignsCount.value += CAMPAIGNS_PAGE_SIZE;
}

const sourcesVisible = ref(false);

// Desktop previews a campaign in a modal instead of navigating (the mini card
// emits `open` only at that breakpoint). Holding the id rather than a boolean
// lets the modal mount fresh per campaign, so its one-shot fetch runs for the
// one that was clicked.
const openCampaignId = ref<string | null>(null);

// Bio is stored as HTML on the backend; sanitize before rendering with
// v-html (same DOMPurify helper used by the terms/privacy pages).
const sanitizedBio = computed(() => $sanitizeHtml(candidate.value?.bio));
</script>

<template>
  <div class="candidate-profile-page">
    <PageHero constrained max-width="max-w-6xl">
      <template v-if="candidate">
        <!-- Mobile shows a back arrow in the top nav instead (Figma). -->
        <AppBreadcrumbs
          :custom-breadcrumbs="breadcrumbs"
          :include-home="false"
          class="hidden lg:flex"
        />

        <div
          class="candidate-profile-page__layout flex flex-col items-start gap-6 lg:flex-row"
        >
          <ProfileCard :candidate="candidate" :party-href="partyHref" />

          <!-- min-w-0: a flex item defaults to min-width:auto, so wide panel
               content (a long unbreakable string, a table) would otherwise
               refuse to narrow and squeeze the profile card instead. -->
          <div class="candidate-profile-page__panel w-full min-w-0 flex-1">
            <nav class="candidate-profile-page__tabs flex w-full items-stretch">
              <button
                v-for="tab in TABS"
                :key="tab.value"
                type="button"
                :data-testid="`profile-tab-${tab.value}`"
                class="candidate-profile-page__tab flex-1"
                :class="{
                  'candidate-profile-page__tab--active':
                    activeTab === tab.value,
                }"
                @click="activeTab = tab.value"
              >
                {{ tab.label }}
              </button>
            </nav>

            <!-- About -->
            <div
              v-if="activeTab === 'about'"
              data-testid="profile-panel-about"
              class="candidate-profile-page__section flex flex-col gap-5"
            >
              <div>
                <AppTypography type="body-bold" element="p">Bio</AppTypography>
                <!-- eslint-disable vue/no-v-html -->
                <div
                  class="candidate-profile-page__bio candidate-profile-page__subtext font-body mt-1"
                  v-html="sanitizedBio"
                ></div>
                <!-- eslint-enable vue/no-v-html -->
              </div>

              <div
                v-if="hasWebsite || hasSources"
                class="candidate-profile-page__divider"
              />

              <div v-if="hasWebsite">
                <div class="flex items-center gap-1.5">
                  <AppTypography type="body-bold" element="p"
                    >Website</AppTypography
                  >
                  <Icon
                    name="solar:square-top-down-linear"
                    size="16"
                    class="shrink-0"
                  />
                </div>
                <a
                  :href="websiteHref"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="candidate-profile-page__website mt-1 inline-block"
                >
                  <AppTypography type="body" class="text-color-brand">
                    {{ candidate.website }}
                  </AppTypography>
                </a>
              </div>

              <button
                v-if="hasSources"
                type="button"
                data-testid="sources-button"
                class="candidate-profile-page__sources flex items-center justify-between"
                @click="sourcesVisible = true"
              >
                <span class="flex items-center gap-2">
                  <Icon name="solar:link-round-linear" class="shrink-0" />
                  <AppTypography type="caption-bold" element="span"
                    >Sources</AppTypography
                  >
                </span>
                <Icon name="solar:alt-arrow-right-linear" class="shrink-0" />
              </button>

              <ReportProblemLink
                report-type="candidates"
                :report-id="candidate?.id"
                class="mt-4"
              />
            </div>

            <!-- Campaigns -->
            <div
              v-else-if="activeTab === 'campaigns'"
              data-testid="profile-panel-campaigns"
              class="candidate-profile-page__section flex flex-col gap-4"
            >
              <div>
                <AppTypography type="body-bold" element="p">
                  Associated Campaigns
                </AppTypography>
                <AppTypography
                  type="body"
                  element="p"
                  class="candidate-profile-page__subtext mt-1"
                >
                  Campaigns associated with this political party.
                </AppTypography>
              </div>

              <template v-if="visibleCampaigns.length">
                <div class="grid grid-cols-2 gap-2 lg:grid-cols-3 lg:gap-4">
                  <CampaignMiniCard
                    v-for="campaign in visibleCampaigns"
                    :key="campaign.id"
                    :campaign="campaign"
                    @open="openCampaignId = campaign.id"
                  />
                </div>

                <div v-if="hasMoreCampaigns" class="flex justify-center">
                  <button
                    type="button"
                    data-testid="show-more-campaigns"
                    class="candidate-profile-page__show-more"
                    @click="showMoreCampaigns"
                  >
                    <AppTypography type="subheadline-bold" element="span">
                      Show More
                    </AppTypography>
                  </button>
                </div>
              </template>

              <div
                v-else
                data-testid="campaigns-empty-state"
                class="candidate-profile-page__empty flex flex-col items-center gap-3 text-center"
              >
                <img
                  src="/no-associated-items.svg"
                  width="195"
                  height="166"
                  alt=""
                  aria-hidden="true"
                  class="candidate-profile-page__empty-illustration"
                />
                <AppTypography type="title-6">No Campaigns Yet</AppTypography>
                <AppTypography type="body" class="text-color-tertiary">
                  There are no campaigns to display at the moment.
                </AppTypography>
              </div>
            </div>

            <!-- Lobbyists -->
            <div
              v-else
              data-testid="profile-panel-lobbyists"
              class="candidate-profile-page__section flex flex-col gap-4"
            >
              <div>
                <AppTypography type="body-bold" element="p">
                  Supporting Lobbyists
                </AppTypography>
                <AppTypography
                  type="body"
                  element="p"
                  class="text-color-subtext mt-1"
                >
                  Lobbying organizations and individuals associated to this
                  political party.
                </AppTypography>
              </div>

              <template v-if="lobbyists.length">
                <div class="flex flex-col gap-3">
                  <LobbyistRow
                    v-for="lobbyist in lobbyists"
                    :key="lobbyist.id"
                    :lobbyist="lobbyist"
                  />
                </div>

                <div class="candidate-profile-page__note">
                  <AppTypography
                    type="caption-bold"
                    element="p"
                    class="uppercase"
                  >
                    Note:
                  </AppTypography>
                  <AppTypography type="footnote" element="p" class="mt-1">
                    Affiliation data is drawn from public disclosure registers
                    only. Inclusion does not imply wrongdoing or any formal
                    relationship with this candidate personally.
                  </AppTypography>
                </div>

                <button
                  v-if="hasSources"
                  type="button"
                  data-testid="sources-button-lobbyists"
                  class="candidate-profile-page__sources candidate-profile-page__sources--lobbyists flex items-center justify-between"
                  @click="sourcesVisible = true"
                >
                  <span class="flex items-center gap-2">
                    <Icon name="solar:link-round-bold" class="shrink-0" />
                    <AppTypography type="caption-bold" element="span"
                      >Sources</AppTypography
                    >
                  </span>
                  <Icon name="solar:alt-arrow-right-linear" class="shrink-0" />
                </button>
              </template>

              <div
                v-else
                data-testid="lobbyists-empty-state"
                class="candidate-profile-page__empty flex flex-col items-center gap-3 text-center"
              >
                <img
                  src="/no-associated-items.svg"
                  width="195"
                  height="166"
                  alt=""
                  aria-hidden="true"
                  class="candidate-profile-page__empty-illustration"
                />
                <AppTypography type="title-6">No Lobbyists Yet</AppTypography>
                <AppTypography type="body" class="text-color-tertiary">
                  There are no lobbyists to display at the moment.
                </AppTypography>
              </div>
            </div>
          </div>
        </div>
      </template>

      <div
        v-else-if="candidatePending"
        data-testid="candidate-loading"
        class="candidate-profile-page__layout flex flex-col items-start gap-6 lg:flex-row"
      >
        <AppSkeletonLoader
          :items="[{ height: '420px' }]"
          container-class="w-full lg:w-[360px] lg:shrink-0"
        />
        <AppSkeletonLoader
          :items="[{ height: '480px' }]"
          container-class="w-full min-w-0 flex-1"
        />
      </div>
      <div
        v-else
        data-testid="candidate-not-found"
        class="candidate-profile-page__not-found flex flex-col items-center gap-3 text-center"
      >
        <AppTypography type="title-5">Candidate not found</AppTypography>
        <NuxtLink
          to="/candidates"
          class="candidate-profile-page__back no-underline"
        >
          <Icon name="solar:arrow-left-linear" class="shrink-0" />
          <AppTypography type="body-bold" element="span"
            >Back to Candidates</AppTypography
          >
        </NuxtLink>
      </div>
    </PageHero>

    <SourcesDialog
      v-model:visible="sourcesVisible"
      :sources="candidate?.sources ?? []"
    />
    <CampaignDetailModal
      v-if="openCampaignId"
      :key="openCampaignId"
      :campaign-id="openCampaignId"
      :visible="true"
      @update:visible="openCampaignId = null"
    />
  </div>
</template>

<style scoped lang="scss">
.candidate-profile-page {
  display: flex;
  flex-direction: column;
  flex: 1 0 auto;
  min-height: calc(100vh - var(--header-height));

  &__panel {
    // Mobile (Figma): the tabs + content sit directly on the sand band with
    // no card wrapper. The white rounded card is a desktop-only treatment.
    @media (min-width: 1024px) {
      padding: $spacing-2xl;
      border-radius: $radius-2xl;
      background-color: $surface-color-bg;
    }
  }

  &__tabs {
    // Figma: complementary/darker-shade-01 (#766035) — no matching design
    // token, so set the literal value.
    border-bottom: 1px solid #766035;
  }

  &__tab {
    @include font-subheadline;
    padding: $spacing-sm;
    border: none;
    border-bottom: 4px solid transparent;
    background: transparent;
    color: #766035;
    font-weight: 600;
    text-align: center;
    cursor: pointer;

    &--active {
      border-bottom-color: $text-color-brand;
      color: $text-color-brand;
    }
  }

  &__section {
    padding-top: $spacing-xl;
  }

  &__bio {
    // The sanitized bio may contain block tags (<p>, <ul>...). Keep paragraph
    // spacing readable; the browser's default <p> margins would otherwise be
    // reset away by the global reset.
    :deep(p) {
      margin: 0;
    }

    :deep(p + p) {
      margin-top: $spacing-md;
    }
  }

  &__divider {
    height: 1px;
    // Figma: borders/default (#e8e3db) — this app's `$border-color` token is
    // a different (navy) palette used elsewhere, so set the literal value.
    background-color: #e8e3db;

    @media (max-width: 639.98px) {
      background-color: $complementary-color-shade-1;
    }
  }

  &__subtext {
    // Figma: text/primary (#201e1a) — this app's $text-color-secondary is a
    // different (navy) palette.
    color: #201e1a;
  }

  // Figma: "Show More" is a plain underlined text link centred under the
  // grid, not the outlined pill this used to render.
  &__show-more {
    border: none;
    background: transparent;
    color: $text-color-primary;
    cursor: pointer;
    text-decoration: underline;
  }

  &__sources {
    width: 100%;
    // No design value to hand — capped so the chevron doesn't drift to the far
    // edge of the panel. Revisit against the Figma frame.
    max-width: 400px;
    padding: $spacing-md $spacing-lg;
    // Figma: borders/secondary (#827b71) — this app's `$border-color` token
    // is a different, unrelated pale-grey value, so use the warm-grey tag
    // token instead.
    border: 1px solid $border-color-tag;
    border-radius: $radius-sm;
    background: transparent;
    cursor: pointer;

    &--lobbyists {
      max-width: 100%;
      height: 48px;

      @media (min-width: 1024px) {
        max-width: 320px;
      }
    }
  }

  &__note {
    padding: $spacing-lg;
    border-radius: $radius-md;
    background-color: $border-color-tag;
    color: white;
  }

  &__empty {
    padding: $spacing-6xl $spacing-lg;

    &-illustration {
      width: 100%;
      max-width: 195px;
      height: auto;
    }
  }

  &__not-found {
    padding: $spacing-6xl $spacing-lg;
  }

  &__back {
    display: flex;
    align-items: center;
    gap: $spacing-sm;
    color: $text-color-brand;

    &.router-link-active {
      background-color: transparent;
    }
  }
}
</style>
